import { summarizeMatchedSchedules } from "@common/helpers/domains/accounting/matchedSchedulesSummary";
import { pluralizeWithCount } from "@common/helpers/string";
import { MatchedSchedule } from "@common/schemas/domains/accounting/scheduleMatchedDisplay.schema";
import { ScheduleCard, ScheduleCardVia } from "./ScheduleCard";
import {
    buildScheduleCardCopy,
    formatScheduleCardText,
    SEGMENT_SEPARATOR,
} from "./scheduleCardCopy";
import styles from "./MatchedScheduleCard.module.scss";

export type MatchedCounterpart = "invoice" | "transaction";

interface Props {
    schedules: MatchedSchedule[];
    counterpart: MatchedCounterpart;
}

function formatScheduleLine(schedule: MatchedSchedule) {
    return formatScheduleCardText(
        buildScheduleCardCopy({
            name: [schedule.sourceReference, schedule.scheduleName]
                .filter((segment): segment is string => segment !== null)
                .join(SEGMENT_SEPARATOR),
            termEnd: schedule.termEnd,
            remainingAmount: schedule.remainingAmount,
            status: schedule.status,
            remainingSuffix: "remaining",
        }),
    );
}

/**
 * The schedules living on the other side of a confirmed match (Figma
 * 9980:6829). They can only be changed from the side that owns them, so the
 * card never opens a picker; its tooltip names each one.
 */
export function MatchedScheduleCard({ schedules, counterpart }: Props) {
    const summary = summarizeMatchedSchedules(schedules);
    const countLabel = pluralizeWithCount(counterpart, summary.sourceCount);

    return (
        <ScheduleCard
            schedule={{
                name: `Via ${countLabel}`,
                termEnd: summary.termEnd,
                remainingAmount: summary.remainingAmount,
                status: summary.status ?? undefined,
                remainingSuffix: "remaining",
            }}
            leading={<ScheduleCardVia label={countLabel} />}
            tooltipContent={schedules.map((schedule) => (
                <div key={schedule.scheduleApplicationId}>
                    {formatScheduleLine(schedule)}
                </div>
            ))}
            tooltipClassName={styles.tooltip}
            aria-disabled
        />
    );
}
